import "./style.scss";

import { Tooltip } from "@mui/material";
import { SmallBody } from "helpers/TypographyComponents";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useNormalizedCompanyUsers } from "hooks/useNormalizedCompanyUsers";
import type { DOMConversionMap, NodeKey } from "lexical";
import { DecoratorNode } from "lexical";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import { getTemplateSignerEnabled, getTemplateSignerText } from "../../../AgreementSettingsTab/signerHeaderUtils";

import type { TBeneficiariesFormData } from "../..";

/**
 * Create a signature block node that cannot be edited
 */
export class SignatureBlockNode extends DecoratorNode<JSX.Element> {
    __companyName: string;
    /**
     * Signatories data for multi-signature
     */
    __beneficiariesFormData: TBeneficiariesFormData | undefined;
    /**
     * If signature block is called from create agreement template (settings)
     */
    __isAgreementSettings: boolean;
    /**
     * If signature block is called from agreement validation page
     */
    __isAgreementEdit: boolean;
    __templateId: number | undefined;

    constructor(
        companyName: string,
        beneficiariesFormData?: TBeneficiariesFormData | undefined,
        isAgreementSettings: boolean = false,
        isAgreementEdit: boolean = false,
        templateId?: number,
        key?: NodeKey,
    ) {
        super(key);
        this.__companyName = companyName;
        this.__beneficiariesFormData = beneficiariesFormData;
        this.__isAgreementSettings = isAgreementSettings;
        this.__isAgreementEdit = isAgreementEdit;
        this.__templateId = templateId;
    }

    static getType() {
        return "signature-block";
    }

    static clone(node) {
        return new SignatureBlockNode(
            node.__companyName,
            node.__beneficiariesFormData,
            node.__isAgreementSettings,
            node.__isAgreementEdit,
            node.__templateId,
            node.__key,
        );
    }

    static importJSON(serializedNode) {
        return new SignatureBlockNode(serializedNode.title);
    }

    static importDOM(): DOMConversionMap | null {
        return {
            div: (domNode) => {
                if (domNode.className === "non-editable-div") {
                    return {
                        conversion: $convertSignatureBlockElement,
                        priority: 1,
                    };
                }
                return null;
            },
        };
    }

    createDOM(config) {
        const div = document.createElement("div");
        div.className = "lexical-non-editable-signature";
        div.contentEditable = "false";
        div.innerHTML = `
            <div class="signature-location"></div>
        `;

        return div;
    }

    updateDOM() {
        return false;
    }

    exportJSON() {
        return {
            type: "signature-block",
            version: 1,
            companyName: this.__companyName,
        };
    }
    decorate() {
        return (
            <SignatureBlock
                companyName={this.__companyName}
                beneficiariesFormData={this.__beneficiariesFormData}
                isAgreementSettings={this.__isAgreementSettings}
                isAgreementEdit={this.__isAgreementEdit}
                templateId={this.__templateId}
            />
        );
    }
}

/**
 * Signature block jsx component
 */
interface ISignatureBlock {
    /**
     * Name of the company
     */
    companyName: string;
    /**
     * Selected beneficiaries for multi-signature data
     */
    beneficiariesFormData: TBeneficiariesFormData | undefined;
    /**
     * If signature block is called from create agreement template (settings)
     */
    isAgreementSettings: boolean;
    /**
     * If signature block is called from agreement validation page
     */
    isAgreementEdit: boolean;
    templateId?: number;
}
const SignatureBlock = ({
    companyName,
    beneficiariesFormData,
    isAgreementSettings,
    isAgreementEdit,
    templateId,
}: ISignatureBlock) => {
    const [t] = useTranslation();
    const { getCompanySettings } = useCompanySettings();
    const userList = useNormalizedCompanyUsers();
    const rawSettings = useAppSelector((state) => state.settings.companySettings?.settings ?? []);
    const showSignatoryJobTitle = getCompanySettings("agreements_show_signatory_job_title");
    const masterEnabled = !!getCompanySettings("agreements_internal_signer_header_text_enabled");
    const defaultEnabled = !!getCompanySettings("agreements_internal_signer_header_text_enabled_default");
    const rawSignerHeaderText = getCompanySettings("agreements_internal_signer_header_text");

    let signerHeaderText = "";
    if (masterEnabled) {
        if (templateId && getTemplateSignerEnabled(rawSettings, templateId)) {
            signerHeaderText = getTemplateSignerText(rawSettings, templateId);
        } else if (defaultEnabled && typeof rawSignerHeaderText === "string") {
            signerHeaderText = rawSignerHeaderText;
        }
    }
    const { beneficiaryIds, titleBeneficiaries } = beneficiariesFormData || {};

    const companySigner = userList.find((user) => +user.id === beneficiaryIds?.[0]);
    const { firstname, lastname, jobTitle } = companySigner ?? {};

    return (
        <div className="signatures-lexical-block" id="lexical-signatures" contentEditable={false}>
            <SmallBody color="info" style={{ marginBottom: "0.5rem" }}>
                {t("RichEditor.PlacementOfSignature")}
            </SmallBody>
            <div className="lexical-signatures">
                {!isAgreementEdit && (
                    <div className="lexical-signatures-block">
                        <SmallBody color="grey" style={signerHeaderText ? { whiteSpace: "pre-wrap" } : undefined}>
                            {signerHeaderText || `${t("RichEditor.InternalSignatory")} (${companyName})`}
                        </SmallBody>
                        {!signerHeaderText && showSignatoryJobTitle && (
                            <Tooltip title={t("RichEditor.ConfigureJobTitleInfo")}>
                                <div className="title-chip">{t("RichEditor.SignatoryJobTitle")}</div>
                            </Tooltip>
                        )}
                    </div>
                )}
                {isAgreementSettings ? (
                    <div className="lexical-signatures-block">
                        <SmallBody color="grey">{t("RichEditor.ContractBeneficiary")}</SmallBody>
                    </div>
                ) : (
                    <>
                        {beneficiaryIds?.map((id, index) => {
                            const isSignerIndex = index === 0;
                            if (isSignerIndex && isAgreementEdit && companySigner) {
                                return (
                                    <div className="lexical-signatures-block" key={`signature-box-${id}`}>
                                        <SmallBody color="grey">
                                            {t("RichEditor.CompanySignatoryLabel", {
                                                companyName,
                                                firstname,
                                                lastname,
                                            })}
                                            {jobTitle && showSignatoryJobTitle
                                                ? t("RichEditor.CompanySignatoryJobTitleLabel", { jobTitle })
                                                : ""}
                                        </SmallBody>
                                    </div>
                                );
                            }
                            return (
                                <div className="lexical-signatures-block" key={`signature-box-${id}`}>
                                    <SmallBody color="grey">{titleBeneficiaries?.beneficiary[id]?.name}</SmallBody>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
};

function $convertSignatureBlockElement(domNode) {
    const title = domNode.textContent;
    return { node: $createSignatureBlockNode(title) };
}

export function $createSignatureBlockNode(
    companyName: string,
    beneficiariesFormData?: TBeneficiariesFormData,
    isAgreementSettings: boolean = false,
    isAgreementEdit: boolean = false,
    templateId?: number,
) {
    return new SignatureBlockNode(companyName, beneficiariesFormData, isAgreementSettings, isAgreementEdit, templateId);
}

export function $isSignatureBlockNode(node) {
    return node instanceof SignatureBlockNode;
}
